// src/pages/AppSignIn.jsx (3.82.0, owner, Nov 4)
// The CP Fax app signs in through this portal (utils/appHandoff.js). Signed
// in here, this asks for a one-time code with the app's challenge and sends
// the browser to the app's listener on this device. The code is useless
// without the verifier, which never leaves the app.
//
// ask (owner, Nov 4): they were already signed in when this opened, so nothing
// shows they meant it: any program on this device could open the link and
// listen. One tap first. Someone who just signed in on this page has no need.
//
// A session that itself came from a code (the app opened this portal) can't
// make this one: otherwise signing out of the app and pressing a button would
// sign it back in. So that session is signed out here, and the sign-in page
// shows with the request kept; after a real sign-in this page finishes by itself.
import { useEffect, useRef, useState } from 'react';
import AuthShell, { AuthMessage, A } from '../components/AuthShell';
import { S } from './ChooseOwnPassword';
import { createAppSignInCode } from '../utils/api';
import { appCallbackUrl, endAppSignIn } from '../utils/appHandoff';

export default function AppSignIn({ request, user, ask, getToken, signOut, onDone }) {
  const [realSignIn, setRealSignIn] = useState(null);   // null until the token has been read
  const [confirmed, setConfirmed] = useState(!ask);
  const [failed, setFailed] = useState(false);
  const checked = useRef(false), started = useRef(false);
  useEffect(() => {
    if (checked.current) return;
    checked.current = true;
    user.getIdTokenResult()
      .then(({ claims }) => {
        const fromCode = !!(claims.appHandoff || claims.appSignin);
        if (fromCode) signOut();
        setRealSignIn(!fromCode);
      })
      .catch(() => setRealSignIn(true));   // the server refuses it if not, and this page says so
  }, [user, signOut]);
  useEffect(() => {
    if (!realSignIn || !confirmed || started.current) return;
    started.current = true;
    endAppSignIn();
    createAppSignInCode(request.challenge, getToken)
      // a moment to read this, and for the sign-in to finish being logged
      .then(({ code }) => setTimeout(() => window.location.replace(appCallbackUrl(request, code)), 800))
      .catch(e => { console.warn('[app sign-in]', e.message); setFailed(true); });
  }, [realSignIn, confirmed, request, getToken]);
  const cancel = () => { endAppSignIn(); onDone(); };

  if (!realSignIn) return null;   // a moment: the token is kept in the browser
  return (
    <AuthShell stack>
      {!confirmed ? (
        <>
          <h1 style={S.title}>Sign in to the CP Fax app?</h1>
          <p style={S.text}>This signs the app in as <strong>{user.email}</strong>. Only continue if you started this from the app.</p>
          <button onClick={() => setConfirmed(true)} className="auth-btn">Sign in to the app</button>
          <button onClick={cancel} style={A.link} className="auth-link">Cancel</button>
        </>
      ) : failed ? (
        <>
          <AuthMessage tone="error">We couldn't finish signing in to the app. Please go back to the app and try again.</AuthMessage>
          <button onClick={onDone} className="auth-btn">Go to the Fax Portal</button>
        </>
      ) : (
        <>
          <h1 style={S.title}>You're signed in</h1>
          <p style={S.text}>You can close this window.</p>
        </>
      )}
    </AuthShell>
  );
}
